// Alert chime for new task / lead assignments. Synthesised with the Web Audio API, so there is
// no sound file to ship or load and it works the same on the website and inside the Android app.
//
// Browsers only allow sound after the person has touched the page once (click / key / tap), so
// the audio engine is unlocked on the first such gesture. If an alert arrives before that
// (page opened fresh, nobody has clicked yet) the pop-up still shows, just silently.

const KEY = 'cx-notif-sound';
const VOLUME = 0.6;      // note loudness, 0..1 (a compressor below keeps loud overlaps from distorting)
const MIN_GAP_MS = 2500; // several alerts arriving together make one chime, not a machine gun

let ctx = null;
let lastPlayed = 0;

export const isSoundEnabled = () => {
  try { return localStorage.getItem(KEY) !== 'off'; } catch (_) { return true; }
};
export const setSoundEnabled = (on) => {
  try { localStorage.setItem(KEY, on ? 'on' : 'off'); } catch (_) { /* ignore */ }
};

function engine() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  return ctx;
}

export function unlockAudioOnFirstGesture() {
  const events = ['pointerdown', 'keydown', 'touchstart'];
  const unlock = () => {
    const c = engine();
    if (c && c.state === 'suspended') c.resume().catch(() => {});
    if (c && c.state !== 'closed') events.forEach(e => window.removeEventListener(e, unlock, true));
  };
  events.forEach(e => window.addEventListener(e, unlock, true));
  return () => events.forEach(e => window.removeEventListener(e, unlock, true));
}

function note(c, out, freq, start, length, peak) {
  const osc = c.createOscillator();
  const overtone = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine'; osc.frequency.value = freq;
  overtone.type = 'sine'; overtone.frequency.value = freq * 2; // soft bell-like shimmer
  const overtoneGain = c.createGain(); overtoneGain.gain.value = 0.25;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + length);
  osc.connect(gain); overtone.connect(overtoneGain); overtoneGain.connect(gain); gain.connect(out);
  osc.start(start); overtone.start(start);
  osc.stop(start + length + 0.05); overtone.stop(start + length + 0.05);
}

// "ding-dong", twice. `force` bypasses the mute switch and the repeat guard (used by the preview).
export function playAlertSound({ force = false } = {}) {
  if (!force && !isSoundEnabled()) return false;
  if (!force && Date.now() - lastPlayed < MIN_GAP_MS) return false;
  const c = engine();
  if (!c) return false;
  if (c.state === 'suspended') c.resume().catch(() => {});
  if (c.state !== 'running') return false; // still locked: nobody has interacted with the page yet
  lastPlayed = Date.now();
  const out = c.createDynamicsCompressor();
  out.connect(c.destination);
  const t = c.currentTime + 0.02;
  [0, 0.75].forEach((offset) => {
    note(c, out, 880.0, t + offset, 0.34, VOLUME);              // A5
    note(c, out, 1318.5, t + offset + 0.2, 0.62, VOLUME * 0.95); // E6
  });
  return true;
}
